import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Gauge, MessageSquare, Image as ImageIcon, Clapperboard, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LOGO_URL } from "@/lib/brand";
import { ThemeToggle } from "@/components/ThemeToggle";
import { api, API } from "@/lib/api";
import { trackVisit } from "@/lib/track";

const TYPE_ICON = { text: MessageSquare, image: ImageIcon, video: Clapperboard };
const TYPE_LABEL = { text: "Texte / LLM", image: "Image", video: "Vidéo" };

export default function Status() {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    trackVisit("/status");
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
    api.get("/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const groups = ["text", "image", "video"].map((t) => ({ type: t, items: models.filter((m) => m.type === t) })).filter((g) => g.items.length);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="status-page">
      <header className="sticky top-0 z-50 border-b border-border bg-background/70 glass">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8 lg:px-16">
          <Link to="/" className="flex items-center gap-2.5" data-testid="status-logo">
            <img src={LOGO_URL} alt="CRÉRÉA" className="h-10 w-10 rounded-lg object-contain ring-1 ring-gold-500/40" />
            <span className="font-display text-lg font-bold tracking-tight">CRÉRÉA</span>
          </Link>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button onClick={() => navigate("/login")} data-testid="status-cta" className="rounded-full bg-gold-500 text-black hover:bg-gold-400"><KeyRound className="mr-1.5 h-4 w-4" />Obtenir une clé</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 hero-radial" />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16">
          <button onClick={() => navigate("/")} className="mb-6 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="status-back"><ArrowLeft className="h-4 w-4" />Accueil</button>
          <div className="flex flex-wrap items-center gap-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2" data-testid="status-badge">
              <span className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot" />
              <span className="font-mono text-sm text-emerald-400">API en ligne</span>
            </span>
            {status && <span className="font-mono text-sm text-muted-foreground">{status.models} modèles disponibles</span>}
          </div>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">Statut & catalogue des modèles</h1>
          <p className="mt-3 max-w-2xl text-muted-foreground">Tous les modèles accessibles via un endpoint unique, compatible OpenAI. Latences indicatives et coût en crédits par appel.</p>
          <p className="mt-4 font-mono text-sm text-muted-foreground">base_url = <span className="text-gold-400">{API}/v1</span></p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-8 lg:px-16">
        {groups.map((g) => {
          const Icon = TYPE_ICON[g.type];
          return (
            <div key={g.type} className="mb-10">
              <div className="mb-4 flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-gold-500/15 text-gold-400"><Icon className="h-4 w-4" /></div>
                <h2 className="font-display text-xl font-semibold">{TYPE_LABEL[g.type]}</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {g.items.map((m, i) => (
                  <motion.div key={m.id} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05 }}
                    className="rounded-2xl border border-border bg-card p-5 transition-colors hover:border-gold-500/50" data-testid={`status-model-${m.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-display text-base font-semibold">{m.label}</p>
                        <span className="font-mono text-[11px] text-muted-foreground">{m.id}</span>
                      </div>
                      <span className="flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" />OK</span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{m.desc}</p>
                    <div className="mt-4 flex items-center justify-between border-t border-border pt-3 font-mono text-xs">
                      <span className="flex items-center gap-3 text-muted-foreground">
                        <span className="flex items-center gap-1"><Gauge className="h-3 w-3" />{m.latency}</span>
                        <span className="rounded bg-secondary px-1.5 py-0.5">{m.provider}</span>
                      </span>
                      <span className="font-semibold text-gold-400">{m.cost} cr.</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          );
        })}
        <div className="mt-6 rounded-2xl border border-gold-500/30 bg-card p-8 text-center gold-halo">
          <h2 className="font-display text-2xl font-bold tracking-tight">Prêt à brancher tout ça ?</h2>
          <p className="mx-auto mt-2 max-w-xl text-muted-foreground">Une clé, tous les modèles. Crédits offerts à l'inscription.</p>
          <Button onClick={() => navigate("/login")} data-testid="status-final-cta" className="mt-6 rounded-full bg-gold-500 text-black hover:bg-gold-400"><KeyRound className="mr-1.5 h-4 w-4" />Obtenir ma clé API</Button>
        </div>
      </section>
    </div>
  );
}
